import { istWochenende, tagKurz, tagesschluessel } from '@netcon-work/shared'
import { useState } from 'react'
import { TabellenUmschalter, useHinweis } from './hinweis'

/**
 * „Erledigt pro Tag" – der Balken, mit dem das Daily anfängt (Konzept Kap. 4.6).
 *
 * Optik aus dem Mockup (`.bars` / `.barcol`): Säulen von einer Grundlinie, Wert
 * über der Kappe, Tag darunter, kein Gitter und keine Y-Achse.
 *
 * ## Drei Entscheidungen, die man am Bild nicht sieht
 *
 * **Eine Serie, eine Farbe.** Alle Säulen tragen den Akzent. Der heutige Tag
 * ist **nicht** eingefärbt, obwohl das Mockup ihn grün zeigt: Grün ist an
 * derselben Ansicht die Quellenfarbe von Work (Zeitleisten-Legende) und liest
 * sich außerdem als „gut". Beides stimmt hier nicht – heute ist nur *unfertig*.
 * Kenntlich gemacht wird das durch die Beschriftung „heute" und eine hellere
 * Fläche hinter der Säule, nicht durch eine zweite Farbe.
 *
 * **Leere Wochenenden fallen weg, belegte nicht.** Das Mockup sagt „letzte 7
 * Arbeitstage". Vier Nullsäulen von vierzehn verschenken das halbe Bild – ein
 * Samstag mit drei erledigten Aufgaben zu verschweigen wäre aber gelogen.
 * Also: Wochenende ausblenden nur, wenn dort auch nichts passiert ist.
 *
 * **Werte nur, solange sie lesbar sind.** Bis 14 Säulen steht über jeder ihr
 * Wert – bei einstelligen Zahlen ist das die beste Ablesbarkeit und ersetzt die
 * Y-Achse. Darüber wird es Gedränge, dann tragen nur Höchstwert und letzter Tag
 * eine Zahl; alles andere liegt in der Sprechblase und in der Tabellensicht.
 */
export function BalkenErledigt({
  tage,
  onTagKlick,
}: {
  tage: { tag: string; anzahl: number }[]
  onTagKlick: (tag: string) => void
}) {
  const { anMark, feld } = useHinweis()
  const [tabelle, setTabelle] = useState(false)
  const heute = tagesschluessel(new Date())

  const gezeigt = tage.filter((t) => !(istWochenende(t.tag) && t.anzahl === 0))
  const versteckt = tage.length - gezeigt.length
  const hoechst = Math.max(1, ...gezeigt.map((t) => t.anzahl))
  const alleWerte = gezeigt.length <= 14

  /**
   * **Ab 30 Tagen wird der Balken zur Verlaufslinie** (19.08.2026, mit dem 90-Tage-Blick).
   *
   * Bei 64 Arbeitstagen bleiben in dieser Kartenbreite gut 7 px je Säule. Der Abstand von
   * 10 px wäre dann breiter als die Säule selbst, und unter jeder stünde ein Datum, das
   * sich mit dem nächsten überlappt.
   *
   * Deshalb: enger Abstand, und **jedes n-te** Datum trägt eine Beschriftung. Nicht gar
   * keine – eine Achse ohne einen einzigen Anhaltspunkt ist ein Muster, kein Zeitverlauf.
   * Die genauen Zahlen stehen weiter in der Sprechblase und in „Als Tabelle"; geklickt
   * wird unverändert **jeder** Tag, auch der ohne Beschriftung.
   */
  const eng = gezeigt.length > 24
  const schritt = eng ? Math.ceil(gezeigt.length / 12) : 1

  return (
    <>
      <div className="flex items-baseline gap-2">
        <b className="text-[13px]">Erledigt pro Tag</b>
        <span className="text-xs text-muted">Team gesamt</span>
        <span className="ml-auto">
          <TabellenUmschalter
            offen={tabelle}
            umschalten={() => setTabelle((o) => !o)}
            diagramm="Erledigt pro Tag"
          />
        </span>
      </div>

      {tabelle ? (
        <div className="overflow-x-auto">
          <table className="min-w-[560px] mt-2 w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="border-b border-line px-1.5 py-[5px] text-left text-[10.5px] font-semibold text-ink2">
                  Tag
                </th>
                <th className="border-b border-line px-1.5 py-[5px] text-right text-[10.5px] font-semibold text-ink2">
                  Erledigte Aufgaben
                </th>
              </tr>
            </thead>
            <tbody>
              {gezeigt.map((t) => (
                <tr key={t.tag}>
                  <td className="border-b border-line px-1.5 py-1.5">{tagKurz(t.tag)}</td>
                  <td className="tabular border-b border-line px-1.5 py-1.5 text-right">
                    {t.anzahl}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={`flex h-[120px] items-end px-1 pt-1.5 ${eng ? 'gap-[3px]' : 'gap-2.5'}`}>
          {gezeigt.map((t, i) => {
            const istHeute = t.tag === heute
            const zeigeWert = alleWerte || t.anzahl === hoechst || istHeute
            /** Von hinten gezählt, damit „heute" und der Rand immer ein Datum tragen. */
            const zeigeTag = (gezeigt.length - 1 - i) % schritt === 0
            return (
              <button
                key={t.tag}
                type="button"
                onClick={() => onTagKlick(t.tag)}
                {...anMark(
                  <>
                    <b>{tagKurz(t.tag)}</b>
                    <br />
                    {t.anzahl === 1 ? '1 Aufgabe erledigt' : `${t.anzahl} Aufgaben erledigt`}
                    {istHeute && ' · Tag läuft noch'}
                    <br />
                    <span className="text-[11px] text-[#cfe0f5]">Klick zeigt die Ereignisse</span>
                  </>,
                )}
                aria-label={`${tagKurz(t.tag)}: ${t.anzahl} erledigt – Ereignisse anzeigen`}
                className={`flex h-full flex-1 cursor-pointer flex-col items-center justify-end gap-1.5 rounded-md ${
                  istHeute ? 'bg-accent-soft/60' : ''
                }`}
              >
                <span className="tabular h-[13px] shrink-0 text-[11px] font-bold">
                  {zeigeWert ? t.anzahl : ''}
                </span>
                {/*
                  Eigene Zeichenfläche zwischen Wert und Beschriftung. Die
                  Balkenhöhe ist ein Prozentsatz davon – löste sie gegen die
                  ganze Kartenhöhe auf, schöbe ein Höchstwert-Balken die
                  Tagesbeschriftung aus dem Bild.
                */}
                <span className="flex w-full min-h-0 flex-1 items-end justify-center">
                  {/* 4 px gerundetes Datenende, eckig an der Grundlinie; ≤ 24 px dick. */}
                  <i
                    className="block w-[70%] max-w-6 rounded-t bg-accent"
                    style={{
                      height: `${Math.max(t.anzahl === 0 ? 0 : 2, (t.anzahl / hoechst) * 100)}%`,
                    }}
                  />
                </span>
                <span
                  className={`shrink-0 text-[10.5px] whitespace-nowrap ${istHeute ? 'font-semibold text-ink2' : 'text-ink2'}`}
                >
                  {istHeute ? 'heute' : zeigeTag ? kurzerTag(t.tag, alleWerte) : ''}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {versteckt > 0 && (
        <p className="mt-2 text-[11px] text-muted">
          {versteckt === 1 ? '1 Wochenendtag' : `${versteckt} Wochenendtage`} ohne Aktivität nicht
          gezeigt. Ein Wochenende mit Aktivität erscheint.
        </p>
      )}
      {feld}
    </>
  )
}

/** „Do 30." bei wenigen Säulen, nur „30." wenn es eng wird. */
function kurzerTag(tag: string, mitWochentag: boolean): string {
  const teile = tag.split('-')
  const nummer = `${teile[2]}.`
  if (!mitWochentag) return nummer
  const kurz = new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${tag}T12:00:00Z`),
  )
  return `${kurz.replace('.', '')} ${nummer}`
}
